import { apiSlice } from "./apiSlice";
import { ProgramEntity } from "../../services/programs/programs.types";

export const programsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // The backend exposes no search params for programs: the page fetches the
    // full list once (cached under a single entry) and filters client-side.
    getPrograms: builder.query<ProgramEntity[], void>({
      query: () => ({
        url: "/programs",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Program" as const, id })),
              { type: "Program" as const, id: "LIST" },
            ]
          : [{ type: "Program" as const, id: "LIST" }],
    }),
    deleteProgram: builder.mutation<void, number>({
      query: (id) => ({
        url: `/programs/${id}`,
        method: "DELETE",
      }),
      // Deleted programs land in the trash, so its cache must refresh too.
      // Error guard: a failed delete must not refetch anything.
      invalidatesTags: (result, error) =>
        error
          ? []
          : [{ type: "Program" as const, id: "LIST" }, "Trash" as const],
    }),
    // children_mode 'duplicate': the new program gets independent copies of
    // its courses, lessons, projects and resources; 'link': the copy
    // references the SAME courses/projects/resources (only the join rows are
    // copied). program_code is unique and required: the copy always takes a
    // new one.
    duplicateProgram: builder.mutation<
      { id: number; title: string },
      {
        id: number;
        title: string;
        children_mode: "duplicate" | "link";
        program_code: string;
      }
    >({
      query: ({ id, title, children_mode, program_code }) => ({
        url: `/programs/${id}/duplicate`,
        method: "POST",
        data: { title, children_mode, program_code },
      }),
      invalidatesTags: (result, error) =>
        error ? [] : [{ type: "Program" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetProgramsQuery,
  useDeleteProgramMutation,
  useDuplicateProgramMutation,
} = programsApi;
